/**
 * IsoRenderer - High performance 64x32 isometric renderer
 * Depth sorts ground, reticles, entities, overhead bars, projectiles, visual spell effects, and floating text.
 * Enhanced with Smooth Mouse Wheel Zoom (0.5x ~ 2.2x), ambient leaves/spores, campfire particles,
 * and mouse tile hover highlighting.
 */

import { EntityState } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { tileToScreen, screenToTile, TILE_WIDTH, TILE_HEIGHT } from './IsoMath';
import { ReticleRenderer } from './ReticleRenderer';
import { SpriteRenderer } from './SpriteRenderer';
import { EventBus } from '../../core/EventBus';
import { isDungeonTile, getDungeonProps } from '../world/DungeonMap';
import { EnvironmentSystem } from '../world/EnvironmentSystem';
import { getBiomeAt } from '../world/BiomeSystem';
import { getZoneData, ZoneData } from '../world/ZoneMapData';
import {
  getTileTypeAt,
  TILE_PROPERTY_SPECS,
  evaluateTileMovementEffect,
} from '../world/TilePropertySystem';
import { housingSystem } from '../world/HousingSystem';
import { housingCatalog } from '../world/HousingCatalog';
import { housingModeController } from '../ui/HousingModeController';

export interface FloatingText {
  id: string;
  x: number;       // Tile X
  y: number;       // Tile Y
  text: string;
  color: string;
  size: number;
  lifetime: number; // In seconds
  age: number;
}

export interface WorldProp {
  id: string;
  type: 'tree' | 'rock_ore' | 'herb_plant' | 'campfire' | 'crypt_wall' | 'torch_pillar' | 'bone_pile' | 'dungeon_gate' | 'loot_chest';
  name: string;
  x: number;
  y: number;
}

export interface Projectile {
  id: string;
  type: 'arrow' | 'fireball' | 'arcane_bolt' | 'lightning' | 'ki_blast' | 'sound_wave' | string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  currentX: number;
  currentY: number;
  speed: number;
  progress: number; // 0 to 1
  duration: number;
  age: number;
  color: string;
  trail: Array<{ x: number; y: number; alpha: number }>;
}

export interface SpellVisualEffect {
  id: string;
  type: string;
  gx: number;
  gy: number;
  radius: number;
  color: string;
  lifetime: number;
  age: number;
  particles?: Array<{
    dx: number;
    dy: number;
    vx: number;
    vy: number;
    size: number;
    alpha: number;
    color: string;
  }>;
}

export interface AmbientParticle {
  x: number;       // Tile X
  y: number;       // Tile Y
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  color: string;
  type: 'leaf' | 'spore';
}

export interface CampfireParticle {
  x: number;       // Screen X
  y: number;       // Screen Y
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  color: string;
  lifetime: number;
  age: number;
}

export class IsoRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private registry: EntityRegistry;
  private reticles: ReticleRenderer;
  private sprites: SpriteRenderer;
  private cameraX = 0;
  private cameraY = 0;
  private floatingTexts: FloatingText[] = [];
  private worldProps: WorldProp[] = [];
  private projectiles: Projectile[] = [];
  private spellEffects: SpellVisualEffect[] = [];

  // Zoom System (Smooth Mouse Wheel: 0.14x full 160x160 zone overview ~ 2.2x close-up)
  private zoom = 1.0;
  private targetZoom = 1.0;
  private preCombatZoom = 1.0;
  private readonly minZoom = 0.14;
  private readonly maxZoom = 2.2;
  private zoomDisplayTimer = 0; // In seconds, shows zoom badge when changed

  // Interactive Hover Tile
  private hoverTile: { x: number; y: number } | null = null;
  private activeHorseRouteWaypoints: Array<{ x: number; y: number }> = [];

  public setActiveHorseRouteWaypoints(wps: Array<{ x: number; y: number }>): void {
    this.activeHorseRouteWaypoints = wps;
  }

  // Ambient Environmental Effects
  private ambientParticles: AmbientParticle[] = [];
  private campfireParticles: CampfireParticle[] = [];

  // Real-time Day/Night, Weather & Season Environment Engine
  private env: EnvironmentSystem = new EnvironmentSystem();
  private activeZoneId = 1;
  private activeZoneData: ZoneData = getZoneData(1);
  private zoneBannerTimer = 4.5;
  private zoneBannerTitle = '🗺️ ZONE 1. 솔라리스 언덕 (솔라리아 구릉지 - Solaris Hills)';
  private zoneBannerSub = '🏰 왕도 솔라리움 성채(33,33) · 🧭 중앙 십자로(80,80) · 🏡 오크헤이븐 마을(118,118) | 🐎 마구간지기 잭슨';

  public getZoneGateways(): Array<{
    dirLabel: string;
    targetZoneId: number;
    targetZoneName: string;
    x: number;
    y: number;
    spawnX: number;
    spawnY: number;
    startX: number;
    endX: number;
    startY: number;
    endY: number;
  }> {
    if (!this.activeZoneData || !this.activeZoneData.exits) {
      return [];
    }
    return this.activeZoneData.exits.map((exit) => {
      const cx = (exit.startX + exit.endX) / 2;
      const cy = (exit.startY + exit.endY) / 2;
      return {
        dirLabel: exit.targetZoneName,
        targetZoneId: exit.targetZoneId,
        targetZoneName: exit.targetZoneName,
        x: cx,
        y: cy,
        spawnX: exit.targetSpawnX,
        spawnY: exit.targetSpawnY,
        startX: exit.startX,
        endX: exit.endX,
        startY: exit.startY,
        endY: exit.endY,
      };
    });
  }

  // Ground Tile Baking Cache for 160x160 Zone Map (0.5x internal bake -> 10240x5120 world coverage)
  private groundCanvas: HTMLCanvasElement | null = null;
  private groundCtx: CanvasRenderingContext2D | null = null;
  private readonly groundBakeScale = 0.5;
  private readonly groundOriginOffsetX = 5200;
  private readonly groundOriginOffsetY = 80;

  constructor(canvas: HTMLCanvasElement, registry: EntityRegistry) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not get 2D context');
    this.ctx = context;
    this.registry = registry;
    this.reticles = new ReticleRenderer();
    this.sprites = new SpriteRenderer();

    this.initWorldProps();
    this.initGroundCache();
    this.initAmbientParticles();
    this.bindEvents();
    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());

    // Mouse wheel zoom listener
    this.canvas.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.16 : 0.86;
    this.targetZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.targetZoom * factor));
    this.zoomDisplayTimer = 2.0; // Show zoom indicator for 2 seconds
  }

  public getZoom(): number {
    return this.zoom;
  }

  public setTargetZoom(val: number): void {
    this.targetZoom = Math.max(this.minZoom, Math.min(this.maxZoom, val));
    this.zoomDisplayTimer = 2.0;
  }

  public setHoverTile(x: number | null, y: number | null): void {
    if (x === null || y === null) {
      this.hoverTile = null;
    } else {
      this.hoverTile = { x, y };
    }
  }

  public getEnvironment(): EnvironmentSystem {
    return this.env;
  }

  public getActiveZoneId(): number {
    return this.activeZoneId;
  }

  public getActiveZoneData(): ZoneData {
    return this.activeZoneData;
  }

  public setActiveZone(zoneId: number): void {
    this.activeZoneId = zoneId;
    this.activeZoneData = getZoneData(zoneId);
    const z = this.activeZoneData;
    const lmNames = z.landmarks.map((l) => `${l.icon} ${l.name}(${l.x},${l.y})`).join(' · ');
    this.zoneBannerTitle = `🗺️ ZONE ${z.id}. ${z.name} (${z.englishName})`;
    this.zoneBannerSub = `${lmNames} | 🐎 ${z.stableMaster.name}`;
    this.zoneBannerTimer = 4.5;
    this.initGroundCache();
  }

  private initWorldProps(): void {
    // Decorative campfire around Solaris Hills Crossroads (80, 80)
    this.worldProps = [
      { id: 'fire_crossroads', type: 'campfire', name: '십자로 야영 모닥불', x: 77, y: 82 },
    ];
  }

  private initAmbientParticles(): void {
    // 25 floating windblown leaves and golden spores around the active camera
    for (let i = 0; i < 25; i++) {
      const isLeaf = i % 2 === 0;
      this.ambientParticles.push({
        x: 65 + Math.random() * 30,
        y: 65 + Math.random() * 30,
        vx: 0.3 + Math.random() * 0.4,
        vy: 0.15 + Math.random() * 0.3,
        size: isLeaf ? 2.5 + Math.random() * 2.5 : 1.5 + Math.random() * 1.5,
        alpha: 0.4 + Math.random() * 0.5,
        color: isLeaf ? (Math.random() > 0.5 ? '#84cc16' : '#65a30d') : '#fde047',
        type: isLeaf ? 'leaf' : 'spore',
      });
    }
  }

  /**
   * Maps a ZoneTileType + coordinates to fill and stroke colors.
   */
  private getZoneTileColors(tile: string, tx: number, ty: number): { fill: string; stroke: string; detail?: string } {
    const alt = (tx + ty) % 2 === 0;
    switch (tile) {
      case 'hills_grass':
        return { fill: alt ? '#365314' : '#3f6212', stroke: '#1a2e05', detail: '#4d7c0f' };
      case 'cobblestone':
        return { fill: alt ? '#635641' : '#574b39', stroke: '#3e3425', detail: '#7a6a52' };
      case 'dirt_road':
        return { fill: alt ? '#78350f' : '#713f12', stroke: '#451a03', detail: '#92400e' };
      case 'water':
        return { fill: alt ? '#0284c7' : '#0369a1', stroke: '#075985', detail: '#38bdf8' };
      case 'stone_wall':
        return { fill: alt ? '#475569' : '#334155', stroke: '#1e293b', detail: '#64748b' };
      case 'wooden_wall':
        return { fill: alt ? '#78350f' : '#5b2809', stroke: '#351604', detail: '#92400e' };
      case 'stone_pavement':
        return { fill: alt ? '#475569' : '#334155', stroke: '#1e293b', detail: '#64748b' };
      case 'castle_gate':
        return { fill: '#b45309', stroke: '#fde047', detail: '#f59e0b' };
      case 'house_roof':
        return { fill: alt ? '#991b1b' : '#7f1d1d', stroke: '#450a0a', detail: '#dc2626' };
      case 'farmland':
        return { fill: alt ? '#ca8a04' : '#a16207', stroke: '#713f12', detail: '#fef08a' };
      case 'megalith':
        return { fill: '#334155', stroke: '#38bdf8', detail: '#7dd3fc' };
      case 'altar':
        return { fill: '#1e3a8a', stroke: '#fde047', detail: '#38bdf8' };
      case 'bridge':
        return { fill: alt ? '#92400e' : '#78350f', stroke: '#451a03', detail: '#b45309' };
      case 'fence':
        return { fill: '#713f12', stroke: '#fde047' };
      case 'snow':
        return { fill: alt ? '#bfdbfe' : '#93c5fd', stroke: '#60a5fa', detail: '#ffffff' };
      case 'ice':
        return { fill: alt ? '#38bdf8' : '#7dd3fc', stroke: '#e0f2fe', detail: '#ffffff' };
      case 'sand':
        return { fill: alt ? '#d97706' : '#b45309', stroke: '#92400e', detail: '#fde047' };
      case 'lava':
        return { fill: alt ? '#991b1b' : '#7f1d1d', stroke: '#f97316', detail: '#fde047' };
      case 'swamp':
        return { fill: alt ? '#4c1d95' : '#3b0764', stroke: '#a855f7', detail: '#c084fc' };
      case 'swamp_grass':
        return { fill: alt ? '#134e4a' : '#115e59', stroke: '#0f766e', detail: '#2dd4bf' };
      case 'crystal_floor':
        return { fill: alt ? '#1e3a8a' : '#172554', stroke: '#38bdf8', detail: '#e0f2fe' };
      case 'grass':
      default:
        return { fill: alt ? '#2c4021' : '#24351a', stroke: '#1a2613', detail: '#3a542c' };
    }
  }

  public rebindGroundCache(): void {
    this.initGroundCache();
  }

  /**
   * Pre-bake full 160x160 Zone terrain map onto an off-screen canvas once per zone change.
   * Covers all 25,600 tiles of Zone 1: 솔라리스 언덕 (솔라리아 구릉지) and Zones 2~7.
   */
  private initGroundCache(): void {
    const scale = this.groundBakeScale;
    const width = Math.ceil(10400 * scale);
    const height = Math.ceil(5300 * scale);
    if (!this.groundCanvas) {
      this.groundCanvas = document.createElement('canvas');
    }
    this.groundCanvas.width = width;
    this.groundCanvas.height = height;
    const gctx = this.groundCanvas.getContext('2d');
    if (!gctx) return;
    this.groundCtx = gctx;

    gctx.save();
    gctx.clearRect(0, 0, width, height);
    gctx.scale(scale, scale);

    const ox = this.groundOriginOffsetX;
    const oy = this.groundOriginOffsetY;
    const halfW = TILE_WIDTH / 2;
    const halfH = TILE_HEIGHT / 2;
    const z = this.activeZoneData;
    const rows = z.height;
    const cols = z.width;

    for (let ty = 0; ty < rows; ty++) {
      const row = z.tiles[ty];
      if (!row) continue;
      for (let tx = 0; tx < cols; tx++) {
        const tile = row[tx] || 'grass';
        const colors = this.getZoneTileColors(tile, tx, ty);
        const isHill = tile === 'hills_grass';
        const elevPx = isHill ? 14 : 0; // 언덕 타일 14px 고도 융기

        const sx = (tx - ty) * halfW + ox;
        const sy = (tx + ty) * halfH + oy - elevPx;

        // 언덕인 경우 아래쪽/오른쪽 경계 타일이 언덕이 아니면 입체 흙 절벽면(Cliff Face) 먼저 드로잉
        if (isHill) {
          const southTile = ty < rows - 1 ? z.tiles[ty + 1]?.[tx] : null;
          const eastTile = tx < cols - 1 ? z.tiles[ty]?.[tx + 1] : null;

          // 남서쪽 절벽면 (South-West Face: from (sx-halfW, sy) to (sx, sy+halfH))
          if (southTile !== 'hills_grass') {
            gctx.fillStyle = '#1e3a12';
            gctx.beginPath();
            gctx.moveTo(sx - halfW, sy);
            gctx.lineTo(sx, sy + halfH);
            gctx.lineTo(sx, sy + halfH + elevPx);
            gctx.lineTo(sx - halfW, sy + elevPx);
            gctx.closePath();
            gctx.fill();
            gctx.strokeStyle = '#0f2008';
            gctx.lineWidth = 1;
            gctx.stroke();
          }

          // 남동쪽 절벽면 (South-East Face: from (sx, sy+halfH) to (sx+halfW, sy))
          if (eastTile !== 'hills_grass') {
            gctx.fillStyle = '#2d4d1a';
            gctx.beginPath();
            gctx.moveTo(sx, sy + halfH);
            gctx.lineTo(sx + halfW, sy);
            gctx.lineTo(sx + halfW, sy + elevPx);
            gctx.lineTo(sx, sy + halfH + elevPx);
            gctx.closePath();
            gctx.fill();
            gctx.strokeStyle = '#14290d';
            gctx.lineWidth = 1;
            gctx.stroke();
          }
        }

        // 마름모 상단 지표면
        gctx.beginPath();
        gctx.moveTo(sx, sy - halfH);
        gctx.lineTo(sx + halfW, sy);
        gctx.lineTo(sx, sy + halfH);
        gctx.lineTo(sx - halfW, sy);
        gctx.closePath();

        gctx.fillStyle = colors.fill;
        gctx.fill();
        gctx.strokeStyle = colors.stroke;
        gctx.lineWidth = 1;
        gctx.stroke();

        if (colors.detail && (tx * 7 + ty * 13) % 6 === 0) {
          gctx.fillStyle = colors.detail;
          gctx.fillRect(sx - 3, sy - 2, 4, 2);
        }
      }
    }
    gctx.restore();
  }

  /** 서버에서 받은 다른 플레이어 (EntityRegistry 와 별개로 그리기만 한다) */
  private remotePlayers: EntityState[] = [];
  public setRemotePlayers(list: EntityState[]): void {
    this.remotePlayers = list;
  }

  public getCameraOffset(): { x: number; y: number } {
    return { x: this.cameraX, y: this.cameraY };
  }

  private handleResize(): void {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  private bindEvents(): void {
    // Spawn floating damage numbers
    EventBus.on('COMBAT_HIT', (e) => {
      const target = this.registry.get(e.targetId);
      if (!target) return;
      this.spawnFloatingText(
        target.x,
        target.y,
        `-${e.damage}`,
        e.isFriendlyFire ? '#f97316' : '#f87171',
        e.isFriendlyFire ? 16 : 14
      );
    });

    // Spawn floating heal numbers
    EventBus.on('HEAL_APPLIED', (e) => {
      const target = this.registry.get(e.targetId);
      if (!target) return;
      this.spawnFloatingText(target.x, target.y, `+${e.amount}`, '#4ade80', 16);
    });

    // Spawn floating interruption alert
    EventBus.on('CAST_INTERRUPTED', (e) => {
      const ent = this.registry.get(e.entityId);
      if (!ent) return;
      this.spawnFloatingText(ent.x, ent.y, '💥 INTERRUPTED!', '#ef4444', 16);
    });

    // Spawn floating Mez broken
    EventBus.on('MEZ_BROKEN', (e) => {
      const ent = this.registry.get(e.targetId);
      if (!ent) return;
      this.spawnFloatingText(ent.x, ent.y, '💤💥 MEZ BROKEN!', '#fbbf24', 15);
    });

    // Spawn floating CC
    EventBus.on('CC_APPLIED', (e) => {
      const ent = this.registry.get(e.targetId);
      if (!ent) return;
      const label = e.ccType === 'stun' ? '⚡ STUNNED!' : (e.ccType === 'mez' ? '💤 SLEEPING' : '🕸️ ROOTED');
      this.spawnFloatingText(ent.x, ent.y, label, '#eab308', 15);
    });

    // Projectile listener (Arrows, Fireballs, Arcane, Lightning, Ki blasts, Sound waves)
    EventBus.on('PROJECTILE_SPAWNED', (e) => {
      this.spawnProjectile(e.type, e.fromX, e.fromY, e.toX, e.toY, e.speed || 12.0);
    });

    // Visual spell effects listener (Taunts, Cleaves, Shields, Novas, Blinks, Heals)
    EventBus.on('SPELL_EFFECT_TRIGGERED', (e) => {
      this.spawnSpellEffect(e.type, e.gx, e.gy, e.radius || 2.5, e.color);
    });

    // Resource respawned visual feedback
    EventBus.on('RESOURCE_RESPAWNED', (e) => {
      this.spawnFloatingText(e.x, e.y, '🌱 자원 재생성!', '#4ade80', 16);
      this.spawnSpellEffect('heal', e.x, e.y, 2.0, '#4ade80');
    });

    // Auto-zoom on Combat Enter (150% = 1.5x) and restore on Combat Exit
    EventBus.on('COMBAT_STATE_CHANGED', (e) => {
      if (e.inCombat) {
        this.preCombatZoom = this.targetZoom;
        this.setTargetZoom(1.5);
      } else {
        this.setTargetZoom(this.preCombatZoom || 1.0);
      }
    });
  }

  public spawnFloatingText(
    x: number,
    y: number,
    text: string,
    color: string,
    size = 14
  ): void {
    this.floatingTexts.push({
      id: Math.random().toString(),
      x: x + (Math.random() - 0.5) * 0.4,
      y: y + (Math.random() - 0.5) * 0.4,
      text,
      color,
      size,
      lifetime: 1.2,
      age: 0,
    });
  }

  public spawnProjectile(
    type: string,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    speed = 12.0
  ): void {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const duration = Math.max(0.1, dist / speed);

    let color = '#f59e0b';
    if (type === 'fireball') color = '#ef4444';
    else if (type === 'arcane_bolt') color = '#a855f7';
    else if (type === 'lightning') color = '#38bdf8';
    else if (type === 'ki_blast') color = '#eab308';
    else if (type === 'sound_wave') color = '#2dd4bf';

    this.projectiles.push({
      id: Math.random().toString(),
      type,
      fromX,
      fromY,
      toX,
      toY,
      currentX: fromX,
      currentY: fromY,
      speed,
      progress: 0,
      duration,
      age: 0,
      color,
      trail: [],
    });
  }

  public spawnSpellEffect(
    type: string,
    gx: number,
    gy: number,
    radius = 2.5,
    customColor?: string
  ): void {
    let color = customColor || '#f59e0b';
    let lifetime = 0.5;

    const particles: SpellVisualEffect['particles'] = [];

    if (type === 'taunt_burst') {
      color = '#f59e0b';
      lifetime = 0.6;
    } else if (type === 'holy_nova') {
      color = '#fef08a';
      lifetime = 0.7;
      // Scatter holy particles
      for (let i = 0; i < 16; i++) {
        const ang = (i / 16) * Math.PI * 2;
        particles.push({
          dx: 0,
          dy: 0,
          vx: Math.cos(ang) * 3.5,
          vy: Math.sin(ang) * 3.5,
          size: 3 + Math.random() * 3,
          alpha: 1,
          color: i % 2 === 0 ? '#fef08a' : '#ffffff',
        });
      }
    } else if (type === 'heal' || type === 'buff_aura') {
      color = '#4ade80';
      lifetime = 0.75;
      for (let i = 0; i < 16; i++) {
        const ang = (i / 16) * Math.PI * 2;
        particles.push({
          dx: Math.cos(ang) * 14,
          dy: Math.sin(ang) * 7,
          vx: -Math.sin(ang) * 2.0, // Spiral rotation
          vy: -3.0 - Math.random() * 2.5, // Upward lift
          size: 2.5 + Math.random() * 2.5,
          alpha: 1,
          color: i % 2 === 0 ? '#4ade80' : '#fef08a',
        });
      }
    } else if (type === 'kick_whirl') {
      color = '#10b981';
      lifetime = 0.45;
    } else if (type === 'blink_departure' || type === 'blink_arrival') {
      color = '#c084fc';
      lifetime = 0.5;
      for (let i = 0; i < 12; i++) {
        const ang = (i / 12) * Math.PI * 2;
        particles.push({
          dx: 0,
          dy: 0,
          vx: Math.cos(ang) * 4.0,
          vy: Math.sin(ang) * 4.0,
          size: 3,
          alpha: 1,
          color: '#e879f9',
        });
      }
    }

    this.spellEffects.push({
      id: Math.random().toString(),
      type,
      gx,
      gy,
      radius,
      color,
      lifetime,
      age: 0,
      particles: particles.length > 0 ? particles : undefined,
    });
  }

  public render(dt: number): void {
    const ctx = this.ctx;
    const time = performance.now();

    // Smooth camera tracking
    const player = this.registry.getPlayer();
    if (player) {
      const pScreen = tileToScreen(player.x, player.y, 0, 0);
      const targetCamX = this.canvas.width / 2 - pScreen.x;
      const targetCamY = this.canvas.height / 2 - pScreen.y;
      this.cameraX += (targetCamX - this.cameraX) * 0.15;
      this.cameraY += (targetCamY - this.cameraY) * 0.15;
    }

    // Smooth Zoom interpolation
    this.zoom += (this.targetZoom - this.zoom) * 0.18;
    if (this.zoomDisplayTimer > 0) {
      this.zoomDisplayTimer -= dt;
    }

    // Clear background
    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Apply Screen-Center World Transformation with Zoom
    ctx.save();
    const cx = this.canvas.width / 2;
    const cy = this.canvas.height / 2;
    ctx.translate(cx, cy);
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-cx, -cy);

    // 1. High Performance Ground Blit (Single Draw Call)
    this.renderGroundGrid(ctx);

    // 2. Render Mouse Tile Hover Reticle
    if (this.hoverTile) {
      this.renderTileHighlight(ctx, this.hoverTile.x, this.hoverTile.y, time);
    }

    // 2b. Render Housing Mode Grid Cursor & Overlays (Red Square + White X / Green Ghost)
    if (housingModeController.isHousingActive()) {
      // Draw all currently selected tiles in Claim tab
      if (housingModeController.selectedTab === 'claim') {
        for (const t of housingModeController.claimSelectedTiles.values()) {
          const tScreen = tileToScreen(t.x, t.y, this.cameraX, this.cameraY);
          ctx.fillStyle = 'rgba(250, 204, 21, 0.45)';
          ctx.fillRect(tScreen.x - 16, tScreen.y - 16, 32, 32);
          ctx.strokeStyle = '#facc15';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(tScreen.x - 16, tScreen.y - 16, 32, 32);
        }
      }

      const hScreen = tileToScreen(housingModeController.cursorX, housingModeController.cursorY, this.cameraX, this.cameraY);
      housingModeController.renderCanvasOverlay(ctx, hScreen.x, hScreen.y, 32);
    }

    // 3. Render Ground Reticle for targeted entity
    if (player && player.targetId) {
      const target = this.registry.get(player.targetId);
      if (target && !target.isDead) {
        this.reticles.renderGroundReticle(ctx, target, this.cameraX, this.cameraY, time);
      }
    }

    // 4. Render Ground-level Spell Effects (Taunt rings, nova rings, blink circles, heal pillars)
    this.renderGroundSpellEffects(ctx, dt);

    // 4b. Render Zone Central Landmark Monument, Stable Master NPC & Directional Zone Exit Gateways
    this.renderZoneLandmarksAndGateways(ctx, time);

    // 5. Render Combined Depth-Sorted Renderables (Entities + Campfire / Resources)
    interface RenderableItem {
      depth: number;
      draw: () => void;
    }
    const renderables: RenderableItem[] = [];

    // Add Entities (Players, Companions, Monsters, and Resource Nodes)
    const entities = this.remotePlayers.length ? [...this.registry.getAll(), ...this.remotePlayers] : this.registry.getAll();
    for (const ent of entities) {
      // In Housing Mode, hide player and companions to give unobstructed view of building area
      if (housingModeController.isHousingActive() && (ent.role !== 'monster' && ent.category !== 'monster')) {
        continue;
      }
      renderables.push({
        depth: ent.x + ent.y + 0.1,
        draw: () => {
          this.renderEntity(ctx, ent, time);
        },
      });
    }

    // Add World Props (Campfire, etc.)
    for (const prop of this.worldProps) {
      renderables.push({
        depth: prop.x + prop.y + 0.05,
        draw: () => {
          const pos = tileToScreen(prop.x, prop.y, this.cameraX, this.cameraY);
          this.sprites.drawWorldObject(ctx, prop.type, pos.x, pos.y, time);
        },
      });
    }

    // Add 3D Zone Architectural & Environmental Props (Solarium Citadel Walls/Gate/Palace, Oakhaven Cottages/Stable, Trees, Megaliths)
    for (const zProp of this.activeZoneData.props) {
      renderables.push({
        depth: zProp.x + zProp.y + 0.06,
        draw: () => {
          const pos = tileToScreen(zProp.x, zProp.y, this.cameraX, this.cameraY);
          this.drawZoneProp3D(ctx, zProp.type, zProp.name, pos.x, pos.y, time, (zProp as any).customDraw);
        },
      });
    }

    // Add Placed Housing Objects (Floors, Walls, Furnitures, Props, Facilities)
    const placedHousingList = housingSystem.getPlacedObjectsInZone(this.activeZoneId);
    for (const hObj of placedHousingList) {
      const catItem = housingCatalog.get(hObj.catalogId);
      if (catItem) {
        // If it is a 3D Wall or Roof item, give it proper 3D depth and render isometric cube height!
        if (hObj.tab === 'walls' || hObj.catalogId === 'ext_clay_roof') {
          renderables.push({
            depth: hObj.x + hObj.y + 0.09,
            draw: () => {
              const pos = tileToScreen(hObj.x, hObj.y, this.cameraX, this.cameraY);
              this.draw3DHousingWallOrRoof(ctx, hObj.catalogId, pos.x, pos.y, hObj.rotation);
            },
          });
        } else {
          const depthOffset = hObj.tab === 'floors' ? 0.01 : 0.07;
          renderables.push({
            depth: hObj.x + hObj.y + depthOffset,
            draw: () => {
              const pos = tileToScreen(hObj.x, hObj.y, this.cameraX, this.cameraY);
              catItem.draw(ctx, pos.x, pos.y, 32, hObj.rotation);
            },
          });
        }
      }
    }

    // Add 3D Isometric Wall & Building Structure Cubes (석조벽, 목조벽, 건물 옥상/처마 3D 입체화)
    const z = this.activeZoneData;
    const halfW = TILE_WIDTH / 2;
    const halfH = TILE_HEIGHT / 2;
    
    // 현재 카메라 화면(줌 포함)의 4개 코너를 정확한 월드 타일 좌표로 역변환하여 안전 마진(여유 8타일)을 두고 컬링
    const currentZoom = Math.max(0.1, this.zoom);
    const halfViewW = (this.canvas.width / 2) / currentZoom;
    const halfViewH = (this.canvas.height / 2) / currentZoom;
    const screenCorners = [
      { x: cx - halfViewW, y: cy - halfViewH },
      { x: cx + halfViewW, y: cy - halfViewH },
      { x: cx - halfViewW, y: cy + halfViewH },
      { x: cx + halfViewW, y: cy + halfViewH },
    ];
    let minCornerX = Infinity;
    let maxCornerX = -Infinity;
    let minCornerY = Infinity;
    let maxCornerY = -Infinity;
    for (const sc of screenCorners) {
      const t = screenToTile(sc.x, sc.y, this.cameraX, this.cameraY);
      if (t.x < minCornerX) minCornerX = t.x;
      if (t.x > maxCornerX) maxCornerX = t.x;
      if (t.y < minCornerY) minCornerY = t.y;
      if (t.y > maxCornerY) maxCornerY = t.y;
    }
    const margin = 8;
    const minTX = Math.max(0, Math.floor(minCornerX - margin));
    const maxTX = Math.min(z.width - 1, Math.ceil(maxCornerX + margin));
    const minTY = Math.max(0, Math.floor(minCornerY - margin));
    const maxTY = Math.min(z.height - 1, Math.ceil(maxCornerY + margin));

    for (let ty = minTY; ty <= maxTY; ty++) {
      const row = z.tiles[ty];
      if (!row) continue;
      for (let tx = minTX; tx <= maxTX; tx++) {
        const tile = row[tx];
        if (tile === 'stone_wall' || tile === 'wooden_wall' || tile === 'house_roof') {
          renderables.push({
            depth: tx + ty + 0.08,
            draw: () => {
              const pos = tileToScreen(tx, ty, this.cameraX, this.cameraY);
              this.draw3DWallOrRoofCube(ctx, tile, tx, ty, pos.x, pos.y);
            },
          });
        }
      }
    }

    // Sort by depth (Continuous Isometric X + Y)
    renderables.sort((a, b) => a.depth - b.depth);

    // Draw all in painter's order
    for (const item of renderables) {
      item.draw();
    }

    // 6. Render Campfire Embers & Smoke
    this.renderCampfireEffects(ctx, dt);

    // 7. Render Projectiles (Arrows, Fireballs, Lightning, Ki Blasts)
    this.renderProjectiles(ctx, dt);

    // 8. Render Ambient Wind Particles (Leaves & Spores)
    this.renderAmbientParticles(ctx, dt, time);

    // 9. Render Overhead Pointer for targeted entity
    if (player && player.targetId) {
      const target = this.registry.get(player.targetId);
      if (target && !target.isDead) {
        this.reticles.renderOverheadPointer(ctx, target, this.cameraX, this.cameraY, time);
      }
    }

    // 10. Render Floating Combat Text
    this.renderFloatingTexts(ctx, dt);

    ctx.restore(); // Restore unzoomed screen space

    // 11. Real-time Environment Simulation & Dynamic Lighting
    this.env.update(dt, this.canvas.width, this.canvas.height);

    // Collect Screen-Space Point Lights for Dynamic Dark-Punching
    const toScreenPt = (tx: number, ty: number) => {
      const base = tileToScreen(tx, ty, this.cameraX, this.cameraY);
      return {
        x: cx + (base.x - cx) * this.zoom,
        y: cy + (base.y - cy) * this.zoom,
      };
    };

    const lightPoints: Array<{ x: number; y: number; radius: number; color?: string; intensity?: number }> = [];

    // 1) Active Player Lantern
    if (player) {
      const pPt = toScreenPt(player.x, player.y);
      lightPoints.push({
        x: pPt.x,
        y: pPt.y - 12 * this.zoom,
        radius: 125 * this.zoom,
        color: 'rgba(253, 230, 138, 0.35)',
        intensity: 1.0,
      });
    }

    // 2) Campfire Light
    const campfire = this.worldProps.find((p) => p.type === 'campfire');
    if (campfire) {
      const fPt = toScreenPt(campfire.x, campfire.y);
      const fireRadius = (135 + Math.sin(time * 0.007) * 15) * this.zoom;
      lightPoints.push({
        x: fPt.x,
        y: fPt.y - 8 * this.zoom,
        radius: fireRadius,
        color: 'rgba(249, 115, 22, 0.45)',
        intensity: 1.15,
      });
    }

    // 3) Torch Pillars in Dungeon & Entrance
    const torches = this.worldProps.filter((p) => p.type === 'torch_pillar');
    for (const torch of torches) {
      const tPt = toScreenPt(torch.x, torch.y);
      const torchRadius = (90 + Math.sin(time * 0.009 + torch.x) * 12) * this.zoom;
      lightPoints.push({
        x: tPt.x,
        y: tPt.y - 14 * this.zoom,
        radius: torchRadius,
        color: 'rgba(251, 146, 60, 0.4)',
        intensity: 0.9,
      });
    }

    // 4) Dungeon Gate Portal
    const gates = this.worldProps.filter((p) => p.type === 'dungeon_gate');
    for (const gate of gates) {
      const gPt = toScreenPt(gate.x, gate.y);
      const gateRadius = (100 + Math.sin(time * 0.005) * 15) * this.zoom;
      lightPoints.push({
        x: gPt.x,
        y: gPt.y - 18 * this.zoom,
        radius: gateRadius,
        color: 'rgba(168, 85, 247, 0.5)',
        intensity: 1.0,
      });
    }

    // Render Dark Ambient Lighting Mask with Destination-Out Point Light Punching
    this.env.renderLightingMask(ctx, this.canvas.width, this.canvas.height, lightPoints);

    // Render Screen-space Weather Effects ONLY when outdoors in field (Silenced in Dungeon/Indoor)
    const isIndoor = player ? isDungeonTile(player.x, player.y) !== null : false;
    if (!isIndoor) {
      this.env.renderWeatherEffects(ctx, this.canvas.width, this.canvas.height);
    }

    // 12. Render Zoom HUD Indicator & Zone Entry Banner in screen space
    this.renderZoomHUD(ctx);
    this.renderZoneEntryBanner(ctx, dt);
  }

  /**
   * 3D Isometric Wall & Roof Cube Renderer
   * Renders realistic stone walls, timber wooden walls, and terracotta building roofs with full 3D height.
   */
  /**
   * 3D Isometric Wall & Roof Renderer for Housing System
   */
  private draw3DHousingWallOrRoof(
    ctx: CanvasRenderingContext2D,
    catalogId: string,
    sx: number,
    sy: number,
    rot: number
  ): void {
    const halfW = TILE_WIDTH / 2; // 32
    const halfH = TILE_HEIGHT / 2; // 16

    if (catalogId === 'wall_castle_stone' || catalogId === 'wall_brick') {
      // 3D 석조 성벽 / 적벽돌 벽 (높이 44px)
      const h = 44;
      const topY = sy - h;
      const isBrick = catalogId === 'wall_brick';

      // 1. SW Face
      ctx.fillStyle = isBrick ? '#991b1b' : '#475569';
      ctx.beginPath();
      ctx.moveTo(sx - halfW, sy);
      ctx.lineTo(sx, sy + halfH);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = isBrick ? '#7f1d1d' : '#1e293b';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Horizontal seams
      ctx.beginPath();
      ctx.moveTo(sx - halfW, sy - 14); ctx.lineTo(sx, sy + halfH - 14);
      ctx.moveTo(sx - halfW, sy - 28); ctx.lineTo(sx, sy + halfH - 28);
      ctx.stroke();

      // 2. SE Face
      ctx.fillStyle = isBrick ? '#7f1d1d' : '#334155';
      ctx.beginPath();
      ctx.moveTo(sx, sy + halfH);
      ctx.lineTo(sx + halfW, sy);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(sx, sy + halfH - 14); ctx.lineTo(sx + halfW, sy - 14);
      ctx.moveTo(sx, sy + halfH - 28); ctx.lineTo(sx + halfW, sy - 28);
      ctx.stroke();

      // 3. Top Face
      ctx.fillStyle = isBrick ? '#b91c1c' : '#64748b';
      ctx.beginPath();
      ctx.moveTo(sx, topY - halfH);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = isBrick ? '#f87171' : '#94a3b8';
      ctx.stroke();

    } else if (catalogId === 'wall_log') {
      // 3D 통나무 벽 (높이 40px)
      const h = 40;
      const topY = sy - h;

      // SW Face
      ctx.fillStyle = '#78350f';
      ctx.beginPath();
      ctx.moveTo(sx - halfW, sy);
      ctx.lineTo(sx, sy + halfH);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#451a03';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(sx - halfW, sy - 12); ctx.lineTo(sx, sy + halfH - 12);
      ctx.moveTo(sx - halfW, sy - 24); ctx.lineTo(sx, sy + halfH - 24);
      ctx.stroke();

      // SE Face
      ctx.fillStyle = '#5b2809';
      ctx.beginPath();
      ctx.moveTo(sx, sy + halfH);
      ctx.lineTo(sx + halfW, sy);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(sx, sy + halfH - 12); ctx.lineTo(sx + halfW, sy - 12);
      ctx.moveTo(sx, sy + halfH - 24); ctx.lineTo(sx + halfW, sy - 24);
      ctx.stroke();

      // Top Face
      ctx.fillStyle = '#92400e';
      ctx.beginPath();
      ctx.moveTo(sx, topY - halfH);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#b45309';
      ctx.stroke();

    } else if (catalogId === 'wall_window') {
      // 3D 창문 벽 (높이 40px + 유리창 렌더링)
      const h = 40;
      const topY = sy - h;

      // Base Wall (SW Face)
      ctx.fillStyle = '#78350f';
      ctx.beginPath();
      ctx.moveTo(sx - halfW, sy);
      ctx.lineTo(sx, sy + halfH);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();

      // Window cutout
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(sx - 16, sy - 28, 14, 18);
      ctx.strokeStyle = '#ffffff';
      ctx.strokeRect(sx - 16, sy - 28, 14, 18);

      // SE Face
      ctx.fillStyle = '#5b2809';
      ctx.beginPath();
      ctx.moveTo(sx, sy + halfH);
      ctx.lineTo(sx + halfW, sy);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.closePath();
      ctx.fill();

      // Top Face
      ctx.fillStyle = '#92400e';
      ctx.beginPath();
      ctx.moveTo(sx, topY - halfH);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();

    } else if (catalogId === 'wall_wood_door') {
      // 3D 현관문 (도어 프레임 + 열린 문)
      const h = 38;
      const topY = sy - h;

      ctx.fillStyle = '#b45309';
      ctx.fillRect(sx - 12, sy - 34, 24, 34);
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath();
      ctx.arc(sx + 6, sy - 17, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#78350f';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(sx - 12, sy - 34, 24, 34);

    } else if (catalogId === 'ext_clay_roof') {
      // 3D 붉은 기와 지붕 피라미드 처마
      const wallH = 28;
      const roofH = 26;
      const topY = sy - wallH;
      const apexY = topY - roofH;

      ctx.fillStyle = '#b91c1c';
      ctx.beginPath();
      ctx.moveTo(sx - halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx, apexY);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#991b1b';
      ctx.beginPath();
      ctx.moveTo(sx, topY + halfH);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, apexY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#7f1d1d';
      ctx.stroke();
    }
  }

  private draw3DWallOrRoofCube(
    ctx: CanvasRenderingContext2D,
    tile: string,
    tx: number,
    ty: number,
    sx: number,
    sy: number
  ): void {
    const halfW = TILE_WIDTH / 2; // 32
    const halfH = TILE_HEIGHT / 2; // 16
    const z = this.activeZoneData;
    const isHill = z.tiles[ty]?.[tx] === 'hills_grass';
    const elev = isHill ? 14 : 0;
    const baseSy = sy - elev;

    if (tile === 'stone_wall') {
      // ======================================================================
      // 3D 석조 성벽/벽체 블록 (Stone Wall: 높이 44px)
      // ======================================================================
      const h = 44;
      const topY = baseSy - h;

      // 1. 좌측 벽면 (South-West Face: 음영 중간)
      ctx.fillStyle = '#475569';
      ctx.beginPath();
      ctx.moveTo(sx - halfW, baseSy);
      ctx.lineTo(sx, baseSy + halfH);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1;
      ctx.stroke();

      // 석조 벽돌 줄눈선 (Horizontal seams on SW face)
      ctx.beginPath();
      ctx.moveTo(sx - halfW, baseSy - 14); ctx.lineTo(sx, baseSy + halfH - 14);
      ctx.moveTo(sx - halfW, baseSy - 28); ctx.lineTo(sx, baseSy + halfH - 28);
      ctx.stroke();

      // 2. 우측 벽면 (South-East Face: 가장 짙은 음영)
      ctx.fillStyle = '#334155';
      ctx.beginPath();
      ctx.moveTo(sx, baseSy + halfH);
      ctx.lineTo(sx + halfW, baseSy);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // 석조 벽돌 줄눈선 on SE face
      ctx.beginPath();
      ctx.moveTo(sx, baseSy + halfH - 14); ctx.lineTo(sx + halfW, baseSy - 14);
      ctx.moveTo(sx, baseSy + halfH - 28); ctx.lineTo(sx + halfW, baseSy - 28);
      ctx.stroke();

      // 3. 상단 마름모 지붕면 (Top Cap: 가장 밝은 반사광)
      ctx.fillStyle = '#64748b';
      ctx.beginPath();
      ctx.moveTo(sx, topY - halfH);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1;
      ctx.stroke();

      // 상단 여장/덮개돌 하이라이트 (Crest detail)
      if ((tx + ty) % 2 === 0) {
        ctx.fillStyle = '#94a3b8';
        ctx.fillRect(sx - 3, topY - 2, 6, 4);
      }

    } else if (tile === 'wooden_wall') {
      // ======================================================================
      // 3D 목조 건물/방책 벽체 (Timber Wooden Wall: 높이 40px)
      // ======================================================================
      const h = 40;
      const topY = baseSy - h;

      // 1. 좌측 목재면 (SW Face: 오크 통나무/판자)
      ctx.fillStyle = '#78350f';
      ctx.beginPath();
      ctx.moveTo(sx - halfW, baseSy);
      ctx.lineTo(sx, baseSy + halfH);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#451a03';
      ctx.lineWidth = 1;
      ctx.stroke();

      // 가로 통나무 판자 라인들
      ctx.beginPath();
      ctx.moveTo(sx - halfW, baseSy - 10); ctx.lineTo(sx, baseSy + halfH - 10);
      ctx.moveTo(sx - halfW, baseSy - 20); ctx.lineTo(sx, baseSy + halfH - 20);
      ctx.moveTo(sx - halfW, baseSy - 30); ctx.lineTo(sx, baseSy + halfH - 30);
      ctx.stroke();

      // 2. 우측 목재면 (SE Face: 깊은 나무 그늘)
      ctx.fillStyle = '#5b2809';
      ctx.beginPath();
      ctx.moveTo(sx, baseSy + halfH);
      ctx.lineTo(sx + halfW, baseSy);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // SE face 판자 라인들
      ctx.beginPath();
      ctx.moveTo(sx, baseSy + halfH - 10); ctx.lineTo(sx + halfW, baseSy - 10);
      ctx.moveTo(sx, baseSy + halfH - 20); ctx.lineTo(sx + halfW, baseSy - 20);
      ctx.moveTo(sx, baseSy + halfH - 30); ctx.lineTo(sx + halfW, baseSy - 30);
      ctx.stroke();

      // 3. 상단 목재면 (Top Cap: 따스한 황토빛 나무 질감)
      ctx.fillStyle = '#92400e';
      ctx.beginPath();
      ctx.moveTo(sx, topY - halfH);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#b45309';
      ctx.lineWidth = 1;
      ctx.stroke();

    } else if (tile === 'house_roof') {
      // ======================================================================
      // 3D 중세 건물 지붕 및 처마 (Terracotta Tiled Roof: 높이 50px + 피라미드 처마)
      // ======================================================================
      const wallH = 34;
      const roofH = 22;
      const topY = baseSy - wallH;
      const apexY = topY - roofH;

      // 1. 지붕 아래 건물 외벽 기초
      ctx.fillStyle = '#64748b';
      ctx.beginPath();
      ctx.moveTo(sx - halfW, baseSy);
      ctx.lineTo(sx, baseSy + halfH);
      ctx.lineTo(sx, topY + halfH);
      ctx.lineTo(sx - halfW, topY);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#475569';
      ctx.beginPath();
      ctx.moveTo(sx, baseSy + halfH);
      ctx.lineTo(sx + halfW, baseSy);
      ctx.lineTo(sx + halfW, topY);
      ctx.lineTo(sx, topY + halfH);
      ctx.closePath();
      ctx.fill();

      // 2. 좌측 붉은 점토 기와면 (SW Roof Slope: 크림슨 테라코타)
      ctx.fillStyle = '#b91c1c';
      ctx.beginPath();
      ctx.moveTo(sx - halfW - 2, topY);
      ctx.lineTo(sx, topY + halfH + 2);
      ctx.lineTo(sx, apexY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#450a0a';
      ctx.lineWidth = 1;
      ctx.stroke();

      // 기와 줄눈선
      ctx.beginPath();
      ctx.moveTo(sx - halfW * 0.5, topY - roofH * 0.4); ctx.lineTo(sx * 0.5 + (sx - halfW) * 0.5, topY + halfH * 0.5);
      ctx.stroke();

      // 3. 우측 붉은 점토 기와면 (SE Roof Slope: 짙은 음영)
      ctx.fillStyle = '#7f1d1d';
      ctx.beginPath();
      ctx.moveTo(sx, topY + halfH + 2);
      ctx.lineTo(sx + halfW + 2, topY);
      ctx.lineTo(sx, apexY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // 4. 지붕 용마루 능선 (Roof Ridge Peak)
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 1.5, apexY - 2, 3, 3);
    }
  }

  /**
   * Renders 3D Isometric Zone Props (Solarium Citadel Walls, Gate, Palace, Cottages, Stable, Megaliths, etc.)
   */
  private drawZoneProp3D(
    ctx: CanvasRenderingContext2D,
    type: string,
    name: string,
    sx: number,
    sy: number,
    time: number,
    customDraw?: (ctx: CanvasRenderingContext2D, sx: number, sy: number, time: number) => void
  ): void {
    ctx.save();
    if (customDraw && typeof customDraw === 'function') {
      try {
        customDraw(ctx, sx, sy, time);
      } catch (err) {
        console.warn('Custom prop draw failed:', err);
      }
      ctx.restore();
      return;
    }
    if (type === 'castle_wall') {
      // ======================================================================
      // 2층 입체 대형 석조 성벽 (Two-Story Citadel Rampart: 총 높이 ~78px, 너비 46px)
      // ======================================================================
      // [1층 기단 & 하층 성벽: sy to sy - 38]
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx - 23, sy - 38, 23, 38); // 1층 좌측면 (양각)
      ctx.fillStyle = '#334155';
      ctx.fillRect(sx, sy - 38, 23, 38);      // 1층 우측면 (음영)

      // 1층 석조 줄눈선 (Mortar seams)
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sx - 23, sy - 14); ctx.lineTo(sx + 23, sy - 14);
      ctx.moveTo(sx - 23, sy - 26); ctx.lineTo(sx + 23, sy - 26);
      ctx.moveTo(sx - 11, sy - 38); ctx.lineTo(sx - 11, sy - 26);
      ctx.moveTo(sx + 11, sy - 26); ctx.lineTo(sx + 11, sy - 14);
      ctx.stroke();

      // [1층-2층 구분 석조 돌림띠 코니스 몰딩: sy - 38 to sy - 42]
      ctx.fillStyle = '#64748b';
      ctx.fillRect(sx - 25, sy - 42, 50, 4);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx - 25, sy - 42, 50, 1.5); // 상단 반사 하이라이트

      // [2층 상층 성벽: sy - 42 to sy - 70]
      ctx.fillStyle = '#64748b';
      ctx.fillRect(sx - 22, sy - 70, 22, 28); // 2층 좌측면
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx, sy - 70, 22, 28);      // 2층 우측면

      // 2층 줄눈선
      ctx.beginPath();
      ctx.moveTo(sx - 22, sy - 56); ctx.lineTo(sx + 22, sy - 56);
      ctx.stroke();

      // 2층 궁수 총안구 (Arrow slit)
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(sx - 2, sy - 62, 4, 11);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 3, sy - 63, 6, 2);

      // [2층 최상단 방어 여장 / 총안 (Crenellations & Merlons: sy - 70 to sy - 79)]
      ctx.fillStyle = '#64748b';
      ctx.fillRect(sx - 24, sy - 79, 13, 9); // 좌측 여장
      ctx.fillRect(sx - 5, sy - 79, 10, 9);  // 중앙 여장
      ctx.fillRect(sx + 11, sy - 79, 13, 9); // 우측 여장

      // 여장 상단 덮개돌 (Stone Capstones)
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 25, sy - 80, 15, 2);
      ctx.fillRect(sx - 6, sy - 80, 12, 2);
      ctx.fillRect(sx + 10, sy - 80, 15, 2);

      // 솔라리스 왕실 황금 징 (Royal Sun Crest)
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 1.5, sy - 49, 3, 3);

    } else if (type === 'castle_gate') {
      // ======================================================================
      // 2층 왕실 성채 대성문 (Two-Story Grand Citadel Gatehouse: 높이 88px, 너비 76px)
      // ======================================================================
      // [좌측 2층 방어 망루탑]
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx - 38, sy - 84, 22, 84);
      ctx.fillStyle = '#334155';
      ctx.fillRect(sx - 27, sy - 84, 11, 84);
      // 좌측 망루 여장 총안
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx - 40, sy - 92, 11, 8);
      ctx.fillRect(sx - 27, sy - 92, 11, 8);
      // 좌측 솔라리스 왕실 깃발
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(sx - 35, sy - 66, 12, 24);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 31, sy - 58, 4, 8);

      // [우측 2층 방어 망루탑]
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx + 16, sy - 84, 22, 84);
      ctx.fillStyle = '#334155';
      ctx.fillRect(sx + 27, sy - 84, 11, 84);
      // 우측 망루 여장 총안
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx + 16, sy - 92, 11, 8);
      ctx.fillRect(sx + 29, sy - 92, 11, 8);
      // 우측 솔라리스 왕실 깃발
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(sx + 23, sy - 66, 12, 24);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx + 27, sy - 58, 4, 8);

      // [중앙 2층 게이트하우스 본체: sy to sy - 76, 너비 32px]
      ctx.fillStyle = '#64748b';
      ctx.fillRect(sx - 16, sy - 76, 32, 76);
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx, sy - 76, 16, 76);

      // 2층 감시/도개교 제어실 창문
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(sx - 9, sy - 64, 7, 10);
      ctx.fillRect(sx + 2, sy - 64, 7, 10);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 10, sy - 65, 9, 2);
      ctx.fillRect(sx + 1, sy - 65, 9, 2);

      // 중앙 최상단 총안 여장
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx - 16, sy - 82, 9, 6);
      ctx.fillRect(sx - 4, sy - 82, 8, 6);
      ctx.fillRect(sx + 7, sy - 82, 9, 6);

      // 1층 도성 진입 관문 아치 (Grand Portal Arch)
      ctx.fillStyle = '#0f172a';
      ctx.beginPath();
      ctx.arc(sx, sy, 17, Math.PI, 0);
      ctx.fill();

      // 황금 포털 아치 테두리 & 마법 볼텍스 펄스
      const pulse = Math.sin(time * 0.003) * 0.3 + 0.7;
      ctx.strokeStyle = `rgba(56, 189, 248, ${pulse})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // 강철 낙하살 (Portcullis Spikes)
      ctx.fillStyle = '#94a3b8';
      for (let i = -12; i <= 12; i += 6) {
        ctx.fillRect(sx + i - 1, sy - 16, 2, 8);
      }

      // 솔라리스 국왕 황금 왕관 문장
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('👑', sx, sy - 38);

    } else if (type === 'castle_tower') {
      // ======================================================================
      // 3층 대형 방어 감시 망루 (Grand Bastion Watchtower: 높이 96px, 너비 48px)
      // ======================================================================
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx - 24, sy - 78, 24, 78);
      ctx.fillStyle = '#334155';
      ctx.fillRect(sx, sy - 78, 24, 78);

      // 줄눈선 & 중간 돌림띠 몰딩
      ctx.fillStyle = '#64748b';
      ctx.fillRect(sx - 26, sy - 44, 52, 4);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx - 26, sy - 44, 52, 1.5);

      // 3층 원형 전망대 & 여장
      ctx.fillStyle = '#64748b';
      ctx.fillRect(sx - 27, sy - 86, 54, 8);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 27, sy - 94, 10, 8);
      ctx.fillRect(sx - 13, sy - 94, 8, 8);
      ctx.fillRect(sx - 1, sy - 94, 8, 8);
      ctx.fillRect(sx + 11, sy - 94, 8, 8);
      ctx.fillRect(sx + 23, sy - 94, 10, 8);

      // 솔라리스 왕실 코니컬 첨탑 지붕 (선택적 돔 첨탑)
      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.moveTo(sx - 28, sy - 94);
      ctx.lineTo(sx, sy - 124);
      ctx.lineTo(sx + 28, sy - 94);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.stroke();

      // 황금 첨탑 풍향계 & 깃발
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 1.5, sy - 134, 3, 10);
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.moveTo(sx + 1.5, sy - 134);
      ctx.lineTo(sx + 16, sy - 129);
      ctx.lineTo(sx + 1.5, sy - 124);
      ctx.closePath();
      ctx.fill();

    } else if (type === 'citadel_spire') {
      // ======================================================================
      // 솔라리스 왕궁 태양의 대첨탑 (Royal Citadel Spire: 높이 115px)
      // ======================================================================
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 16, sy - 80, 16, 80);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx, sy - 80, 16, 80);

      // 고딕 양식 아치형 스테인드글라스 창
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 4, sy - 60, 8, 20);

      // 푸른 슬레이트 대형 첨탑 지붕
      ctx.fillStyle = '#1e3a8a';
      ctx.beginPath();
      ctx.moveTo(sx - 20, sy - 80);
      ctx.lineTo(sx, sy - 130);
      ctx.lineTo(sx + 20, sy - 80);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.stroke();

      // 황금 태양 심볼
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath();
      ctx.arc(sx, sy - 134, 5, 0, Math.PI * 2);
      ctx.fill();
    } else if (type === 'palace') {
      ctx.fillStyle = '#e2e8f0';
      ctx.fillRect(sx - 48, sy - 64, 96, 64);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx, sy - 64, 48, 64);
      ctx.fillStyle = '#1d4ed8';
      ctx.beginPath();
      ctx.moveTo(sx - 56, sy - 64);
      ctx.lineTo(sx, sy - 105);
      ctx.lineTo(sx + 56, sy - 64);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 2.5;
      ctx.stroke();
    } else if (type === 'house_cottage') {
      ctx.fillStyle = '#d6d3d1';
      ctx.fillRect(sx - 24, sy - 30, 48, 30);
      ctx.fillStyle = '#991b1b';
      ctx.beginPath();
      ctx.moveTo(sx - 30, sy - 30);
      ctx.lineTo(sx, sy - 54);
      ctx.lineTo(sx + 30, sy - 30);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(sx - 10, sy - 20, 8, 8);
      ctx.fillStyle = '#451a03';
      ctx.fillRect(sx + 4, sy - 18, 10, 18);
    } else if (type === 'horse_stable') {
      ctx.fillStyle = '#78350f';
      ctx.fillRect(sx - 26, sy - 28, 52, 28);
      ctx.fillStyle = '#b45309';
      ctx.beginPath();
      ctx.moveTo(sx - 30, sy - 28);
      ctx.lineTo(sx, sy - 46);
      ctx.lineTo(sx + 30, sy - 28);
      ctx.closePath();
      ctx.fill();
      ctx.font = '18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🐎', sx, sy - 6);
    } else if (type === 'windmill') {
      ctx.fillStyle = '#d6d3d1';
      ctx.fillRect(sx - 16, sy - 54, 32, 54);
      ctx.strokeStyle = '#fef08a';
      ctx.lineWidth = 3;
      const ang = time * 0.0015;
      for (let i = 0; i < 4; i++) {
        const a = ang + (i * Math.PI) / 2;
        ctx.beginPath();
        ctx.moveTo(sx, sy - 38);
        ctx.lineTo(sx + Math.cos(a) * 32, sy - 38 + Math.sin(a) * 32);
        ctx.stroke();
      }
    } else if (type === 'megalith' || type === 'altar_stone' || type === 'druid_tomb') {
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx - 10, sy - 38, 20, 38);
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(sx - 3, sy - 26, 6, 12);
    } else if (type === 'crystal_spire' || type === 'portal_gate') {
      ctx.fillStyle = type === 'portal_gate' ? '#a855f7' : '#38bdf8';
      ctx.beginPath();
      ctx.moveTo(sx - 14, sy);
      ctx.lineTo(sx, sy - 52);
      ctx.lineTo(sx + 14, sy);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'campfire') {
      this.sprites.drawWorldObject(ctx, 'campfire', sx, sy, time);
    } else if (type === 'scarecrow' || type === 'scarecrow_straw') {
      // ======================================================================
      // 볏짚 훈련용 허수아비 (테라 아니마 정통 화풍 100% 일치)
      // ======================================================================
      // 1. 오크 목재 십자틀
      ctx.fillStyle = '#451a03';
      ctx.fillRect(sx - 2.5, sy - 38, 5, 38);
      ctx.fillRect(sx - 15, sy - 27, 30, 4);
      // 2. 묶인 볏짚 몸통
      ctx.fillStyle = '#ca8a04';
      ctx.fillRect(sx - 6, sy - 26, 12, 16);
      ctx.fillStyle = '#713f12';
      ctx.fillRect(sx - 5, sy - 20, 10, 2);
      // 3. 푸른 낡은 누더기 셔츠
      ctx.fillStyle = '#1e3a8a';
      ctx.fillRect(sx - 7, sy - 25, 14, 11);
      // 4. 볏짚 머리
      ctx.fillStyle = '#ca8a04';
      ctx.beginPath();
      ctx.arc(sx, sy - 31, 5.5, 0, Math.PI * 2);
      ctx.fill();
      // 5. 밀짚모자
      ctx.fillStyle = '#eab308';
      ctx.beginPath();
      ctx.ellipse(sx, sy - 34, 11, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ca8a04';
      ctx.fillRect(sx - 4.5, sy - 42, 9, 8);
      // 6. 훈련용 과녁 마크
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🎯', sx, sy - 46);

    } else if (type === 'scarecrow_pumpkin') {
      // ======================================================================
      // 할로윈 호박머리 허수아비
      // ======================================================================
      ctx.fillStyle = '#451a03';
      ctx.fillRect(sx - 2.5, sy - 38, 5, 38);
      ctx.fillRect(sx - 14, sy - 26, 28, 4);
      ctx.fillStyle = '#1e1b4b';
      ctx.fillRect(sx - 7, sy - 26, 14, 18);
      ctx.fillStyle = '#ea580c';
      ctx.beginPath();
      ctx.arc(sx, sy - 33, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(sx - 3, sy - 35, 2, 2);
      ctx.fillRect(sx + 1, sy - 35, 2, 2);
      ctx.fillRect(sx - 2, sy - 31, 4, 2);

    } else if (type === 'training_dummy') {
      // ======================================================================
      // 기사 훈련용 목각 인형 (DPS 더미)
      // ======================================================================
      ctx.fillStyle = '#78350f';
      ctx.fillRect(sx - 3, sy - 36, 6, 36);
      ctx.fillStyle = '#b45309';
      ctx.fillRect(sx - 10, sy - 28, 20, 18);
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(sx, sy - 19, 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx - 6, sy - 36, 12, 9);
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('⚔️', sx, sy - 42);

    } else if (type === 'lamp_post') {
      // ======================================================================
      // 중세 석조 가로등
      // ======================================================================
      ctx.fillStyle = '#334155';
      ctx.fillRect(sx - 2, sy - 42, 4, 42);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 5, sy - 46, 10, 8);
      ctx.fillStyle = 'rgba(245, 158, 11, 0.2)';
      ctx.beginPath();
      ctx.arc(sx, sy - 42, 16, 0, Math.PI * 2);
      ctx.fill();

    } else if (type === 'bench_stone') {
      // ======================================================================
      // 왕립 석조 벤치
      // ======================================================================
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx - 14, sy - 8, 4, 8);
      ctx.fillRect(sx + 10, sy - 8, 4, 8);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 16, sy - 12, 32, 4);

    } else {
      // Trees (tree_oak, tree_darkwood, tree_snow_pine, palm_tree)
      this.sprites.drawWorldObject(ctx, 'tree', sx, sy, time);
    }
    ctx.restore();
  }

  /**
   * Renders the current Zone's Landmarks, Stable Master NPCs,
   * and Directional Zone Exit Gateways (Walk into or Click to transition Zones!)
   */
  private renderZoneLandmarksAndGateways(ctx: CanvasRenderingContext2D, time: number): void {
    const z = this.activeZoneData;
    const pulse = Math.sin(time * 0.004) * 3;
    ctx.save();

    // 1. Render All Zone Landmarks (e.g., 중앙 십자로(80,80), 오크헤이븐 마을(118,118), 왕도 솔라리움 성채(33,33))
    for (const lm of z.landmarks) {
      const lmPos = tileToScreen(lm.x, lm.y, this.cameraX, this.cameraY);
      ctx.strokeStyle = 'rgba(250, 204, 21, 0.75)';
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.ellipse(lmPos.x, lmPos.y + 8, 34 + pulse, 17 + pulse * 0.5, 0, 0, Math.PI * 2);
      ctx.stroke();

      const signText = `${lm.icon} [Z${z.id}. ${z.name}] ${lm.name} (${lm.x},${lm.y})`;
      ctx.font = 'bold 11px sans-serif';
      const tw = ctx.measureText(signText).width + 16;
      ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect
        ? ctx.roundRect(lmPos.x - tw / 2, lmPos.y - 44, tw, 20, 5)
        : ctx.rect(lmPos.x - tw / 2, lmPos.y - 44, tw, 20);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#fde047';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(signText, lmPos.x, lmPos.y - 34);
    }

    // 2. Stable Master NPCs (Zone Official Stable + Crossroads Stable + Solarium Gate Stable in Zone 1)
    const stableCoords =
      z.id === 1
        ? [
            { x: 76, y: 83, label: `🐎 십자로 마구간 (잭슨 출장소)` },
            { x: 105, y: 108, label: `🐎 오크헤이븐 마구간 (잭슨 본점)` },
            { x: 50, y: 57, label: `🐎 솔라리움 대성문 마구간 (베르나르)` },
          ]
        : [
            { x: z.stableMaster.x, y: z.stableMaster.y, label: `🐎 ${z.stableMaster.name}` },
            { x: 76, y: 83, label: `🐎 ${z.stableMaster.name} (십자로 마구간)` },
          ];
    for (const sm of stableCoords) {
      const smPos = tileToScreen(sm.x, sm.y, this.cameraX, this.cameraY);
      ctx.fillStyle = 'rgba(245, 158, 11, 0.32)';
      ctx.beginPath();
      ctx.ellipse(smPos.x, smPos.y + 8, 22, 11, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.font = '20px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('🐎', smPos.x, smPos.y - 4);
      ctx.font = 'bold 9.5px sans-serif';
      ctx.fillStyle = '#fef08a';
      ctx.fillText(sm.label, smPos.x, smPos.y - 24);
      ctx.font = 'bold 8.5px sans-serif';
      ctx.fillStyle = '#4ade80';
      ctx.fillText('[클릭: 마구간➔마구간 자동 승마 탑승]', smPos.x, smPos.y - 36);
    }

    // 2b. Render Active Stable-to-Stable Horse Route Dashed Highway Line!
    if (this.activeHorseRouteWaypoints.length > 0) {
      const player = this.registry.getPlayer();
      ctx.save();
      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 3.5;
      ctx.setLineDash([10, 6]);
      ctx.lineDashOffset = -(time * 0.04) % 16;
      ctx.beginPath();
      if (player) {
        const pScr = tileToScreen(player.x, player.y, this.cameraX, this.cameraY);
        ctx.moveTo(pScr.x, pScr.y);
      }
      for (let i = 0; i < this.activeHorseRouteWaypoints.length; i++) {
        const wp = this.activeHorseRouteWaypoints[i];
        const wScr = tileToScreen(wp.x, wp.y, this.cameraX, this.cameraY);
        if (i === 0 && !player) ctx.moveTo(wScr.x, wScr.y);
        else ctx.lineTo(wScr.x, wScr.y);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw destination stable pin at the final waypoint
      const lastWp = this.activeHorseRouteWaypoints[this.activeHorseRouteWaypoints.length - 1];
      const lastScr = tileToScreen(lastWp.x, lastWp.y, this.cameraX, this.cameraY);
      ctx.fillStyle = 'rgba(250, 204, 21, 0.35)';
      ctx.beginPath();
      ctx.ellipse(lastScr.x, lastScr.y, 26 + pulse, 13 + pulse * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // 2c. Tactical Tile Property Showcase Labels (Removed for clean production world)
    ctx.restore();
  }

  private renderZoneEntryBanner(ctx: CanvasRenderingContext2D, dt: number): void {
    if (this.zoneBannerTimer <= 0) return;
    this.zoneBannerTimer -= dt;

    const alpha = Math.min(1, this.zoneBannerTimer * 0.8);
    const w = 620;
    const h = 58;
    const x = (this.canvas.width - w) / 2;
    const y = 58;

    ctx.save();
    ctx.fillStyle = `rgba(15, 23, 42, ${alpha * 0.92})`;
    ctx.strokeStyle = `rgba(245, 158, 11, ${alpha * 0.95})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(x, y, w, h, 10) : ctx.rect(x, y, w, h);
    ctx.fill();
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 16px sans-serif';
    ctx.fillStyle = `rgba(254, 240, 138, ${alpha})`;
    ctx.fillText(this.zoneBannerTitle, x + w / 2, y + 21);

    ctx.font = '11.5px sans-serif';
    ctx.fillStyle = `rgba(186, 230, 253, ${alpha})`;
    ctx.fillText(this.zoneBannerSub, x + w / 2, y + 42);
    ctx.restore();
  }

  /**
   * Blit pre-baked 160x160 terrain cache with 0.02ms latency.
   */
  private renderGroundGrid(ctx: CanvasRenderingContext2D): void {
    if (!this.groundCanvas) return;
    const destX = this.cameraX - this.groundOriginOffsetX;
    const destY = this.cameraY - this.groundOriginOffsetY;
    const drawW = this.groundCanvas.width / this.groundBakeScale;
    const drawH = this.groundCanvas.height / this.groundBakeScale;
    ctx.drawImage(this.groundCanvas, destX, destY, drawW, drawH);
  }

  /**
   * Dynamic Mouse Tile Hover Highlight (2:1 Diamond) + Live Tile Property Tooltip
   */
  private renderTileHighlight(ctx: CanvasRenderingContext2D, tx: number, ty: number, time: number): void {
    const screen = tileToScreen(tx, ty, this.cameraX, this.cameraY);
    const halfW = TILE_WIDTH / 2;
    const halfH = TILE_HEIGHT / 2;

    const tileType = getTileTypeAt(this.activeZoneData, tx, ty);
    const spec = TILE_PROPERTY_SPECS[tileType] || TILE_PROPERTY_SPECS.grass;
    const pulse = 0.55 + 0.3 * Math.sin(time * 0.006);
    const strokeRGB = !spec.walkable
      ? `rgba(239, 68, 68, ${pulse})`
      : tileType === 'lava' || tileType === 'swamp'
      ? `rgba(249, 115, 22, ${pulse})`
      : `rgba(56, 189, 248, ${pulse})`;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(screen.x, screen.y - halfH);
    ctx.lineTo(screen.x + halfW, screen.y);
    ctx.lineTo(screen.x, screen.y + halfH);
    ctx.lineTo(screen.x - halfW, screen.y);
    ctx.closePath();

    ctx.strokeStyle = strokeRGB;
    ctx.lineWidth = 1.8;
    ctx.stroke();

    ctx.fillStyle = !spec.walkable ? `rgba(239, 68, 68, 0.22)` : `rgba(56, 189, 248, ${pulse * 0.14})`;
    ctx.fill();

    // Floating Tile Property Tooltip above hovered tile
    const labelTop = `${spec.icon} [${spec.nameKr}] (${tx}, ${ty}) — ${spec.walkable ? '이동가능' : '🚫이동불가'}`;
    const labelSub = spec.summaryKr;
    ctx.font = 'bold 9.5px sans-serif';
    const w1 = ctx.measureText(labelTop).width;
    ctx.font = '8.5px sans-serif';
    const w2 = ctx.measureText(labelSub).width;
    const boxW = Math.max(w1, w2) + 14;
    const boxH = 28;
    const bx = screen.x - boxW / 2;
    const by = screen.y - halfH - boxH - 6;

    ctx.fillStyle = 'rgba(9, 13, 22, 0.92)';
    ctx.strokeStyle = spec.badgeColor;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(bx, by, boxW, boxH, 5) : ctx.rect(bx, by, boxW, boxH);
    ctx.fill();
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 9.5px sans-serif';
    ctx.fillStyle = spec.badgeColor;
    ctx.fillText(labelTop, screen.x, by + 9);
    ctx.font = '8.5px sans-serif';
    ctx.fillStyle = '#e2e8f0';
    ctx.fillText(labelSub, screen.x, by + 20);

    ctx.restore();
  }

  /**
   * Shimmering glitter sparks for mineable ores and gatherable herbs
   */
  private renderResourceGlitter(ctx: CanvasRenderingContext2D, sx: number, sy: number, time: number, type: string): void {
    const cycle = (time * 0.003 + (sx * 7 + sy * 11)) % (Math.PI * 2);
    if (cycle > 1.2) return; // Only flash briefly

    const sparkAlpha = Math.sin((cycle / 1.2) * Math.PI);
    const sparkX = sx + Math.sin(cycle * 3) * 12;
    const sparkY = sy - 14 + Math.cos(cycle * 3) * 8;

    ctx.save();
    ctx.fillStyle = type === 'rock_ore' ? '#38bdf8' : '#4ade80';
    ctx.globalAlpha = sparkAlpha;
    ctx.shadowColor = ctx.fillStyle;
    ctx.shadowBlur = 6;

    // Small 4-point star sparkle
    ctx.beginPath();
    ctx.arc(sparkX, sparkY, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /**
   * Campfire Embers & Smoke
   */
  private renderCampfireEffects(ctx: CanvasRenderingContext2D, dt: number): void {
    const fireProp = this.worldProps.find((p) => p.type === 'campfire');
    if (!fireProp) return;

    const pos = tileToScreen(fireProp.x, fireProp.y, this.cameraX, this.cameraY);

    // Spawn new embers
    if (Math.random() < 0.35) {
      this.campfireParticles.push({
        x: pos.x + (Math.random() - 0.5) * 10,
        y: pos.y - 8,
        vx: (Math.random() - 0.5) * 8,
        vy: -15 - Math.random() * 20,
        size: 1.5 + Math.random() * 1.5,
        alpha: 1,
        color: Math.random() > 0.4 ? '#f97316' : '#fef08a',
        lifetime: 0.6 + Math.random() * 0.4,
        age: 0,
      });
    }

    // Update and draw embers
    this.campfireParticles = this.campfireParticles.filter((p) => {
      p.age += dt;
      if (p.age >= p.lifetime) return false;

      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const progress = p.age / p.lifetime;
      const alpha = (1 - progress) * p.alpha;

      ctx.save();
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * (1 - progress * 0.5), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      return true;
    });
  }

  /**
   * Ambient Floating Wind Particles (Leaves & Spores)
   */
  private renderAmbientParticles(ctx: CanvasRenderingContext2D, dt: number, time: number): void {
    ctx.save();
    for (const p of this.ambientParticles) {
      p.x += (p.vx + Math.sin(time * 0.001 + p.y) * 0.15) * dt * 3.5;
      p.y += (p.vy + Math.cos(time * 0.001 + p.x) * 0.1) * dt * 3.5;

      // Wrap around world boundary
      if (p.x > 26) p.x = 0;
      if (p.y > 26) p.y = 0;

      const screen = tileToScreen(p.x, p.y, this.cameraX, this.cameraY);

      // Frustum cull
      if (
        screen.x < -40 ||
        screen.x > this.canvas.width + 40 ||
        screen.y < -40 ||
        screen.y > this.canvas.height + 40
      ) {
        continue;
      }

      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;

      if (p.type === 'leaf') {
        // Rotating oval leaf
        ctx.save();
        ctx.translate(screen.x, screen.y);
        ctx.rotate((time * 0.002 + p.x * 2) % (Math.PI * 2));
        ctx.beginPath();
        ctx.ellipse(0, 0, p.size * 1.5, p.size * 0.7, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      } else {
        // Glowing spore
        ctx.beginPath();
        ctx.arc(screen.x, screen.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }

  private renderGroundSpellEffects(ctx: CanvasRenderingContext2D, dt: number): void {
    if (this.spellEffects.length === 0) return;

    this.spellEffects = this.spellEffects.filter((fx) => {
      fx.age += dt;
      if (fx.age >= fx.lifetime) return false;

      const progress = fx.age / fx.lifetime;
      const alpha = 1 - progress;
      const pos = tileToScreen(fx.gx, fx.gy, this.cameraX, this.cameraY);

      ctx.save();

      if (fx.type === 'taunt_burst') {
        // Expanding 2:1 isometric golden ring
        const currentRadius = fx.radius * 32 * progress;
        ctx.beginPath();
        ctx.ellipse(pos.x, pos.y, currentRadius, currentRadius * 0.5, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(245, 158, 11, ${alpha * 0.8})`;
        ctx.lineWidth = 3 * (1 - progress);
        ctx.stroke();

        ctx.fillStyle = `rgba(245, 158, 11, ${alpha * 0.15})`;
        ctx.fill();
      } else if (fx.type === 'holy_nova') {
        // Expanding bright holy shockwave
        const currentRadius = fx.radius * 32 * progress;
        ctx.beginPath();
        ctx.ellipse(pos.x, pos.y, currentRadius, currentRadius * 0.5, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(254, 240, 138, ${alpha})`;
        ctx.lineWidth = 4 * (1 - progress);
        ctx.stroke();

        ctx.fillStyle = `rgba(254, 240, 138, ${alpha * 0.25})`;
        ctx.fill();
      } else if (fx.type === 'kick_whirl' || fx.type === 'cleave_arc') {
        // Spinning green / red arc
        ctx.beginPath();
        const startAng = progress * Math.PI * 4;
        ctx.ellipse(pos.x, pos.y, 28, 14, 0, startAng, startAng + Math.PI);
        ctx.strokeStyle = fx.type === 'kick_whirl' ? `rgba(16, 185, 129, ${alpha})` : `rgba(239, 68, 68, ${alpha})`;
        ctx.lineWidth = 4;
        ctx.stroke();
      } else if (fx.type === 'shield_impact') {
        // Impact pulse
        ctx.beginPath();
        ctx.ellipse(pos.x, pos.y - 15, 18 * (1 + progress * 0.5), 9 * (1 + progress * 0.5), 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(251, 191, 36, ${alpha})`;
        ctx.lineWidth = 3;
        ctx.stroke();
      } else if (fx.type === 'heal' || fx.type === 'buff_aura') {
        // 1. Rising Holy Pillar of Light (Vertical gradient beam)
        const beamH = 65 * (1 - progress * 0.3);
        const grad = ctx.createLinearGradient(pos.x, pos.y, pos.x, pos.y - beamH);
        grad.addColorStop(0, `rgba(74, 222, 128, ${alpha * 0.6})`);
        grad.addColorStop(0.4, `rgba(254, 240, 138, ${alpha * 0.4})`);
        grad.addColorStop(1, 'rgba(255, 255, 255, 0)');

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(pos.x - 14, pos.y);
        ctx.lineTo(pos.x + 14, pos.y);
        ctx.lineTo(pos.x + 10, pos.y - beamH);
        ctx.lineTo(pos.x - 10, pos.y - beamH);
        ctx.closePath();
        ctx.fill();

        // 2. Ground Healing Ring
        ctx.beginPath();
        const r = 22 * (0.6 + progress * 0.4);
        ctx.ellipse(pos.x, pos.y, r, r * 0.5, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(74, 222, 128, ${alpha * 0.8})`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        // 3. Floating Green/Golden Holy Cross Emblem above head
        const crossY = pos.y - 45 - progress * 15;
        ctx.fillStyle = `rgba(254, 240, 138, ${alpha * 0.95})`;
        ctx.shadowColor = '#4ade80';
        ctx.shadowBlur = 8;
        // Vertical cross bar
        ctx.fillRect(pos.x - 2, crossY - 7, 4, 14);
        // Horizontal cross bar
        ctx.fillRect(pos.x - 6, crossY - 4, 12, 4);
        ctx.shadowBlur = 0;
      }

      // Render particle bursts if present
      if (fx.particles) {
        for (const p of fx.particles) {
          p.dx += p.vx * dt * 25;
          p.dy += p.vy * dt * 25;
          ctx.beginPath();
          ctx.arc(pos.x + p.dx, pos.y + p.dy, Math.max(1, p.size * (1 - progress)), 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = alpha;
          ctx.fill();
        }
      }

      ctx.restore();
      return true;
    });
  }

  private renderProjectiles(ctx: CanvasRenderingContext2D, dt: number): void {
    if (this.projectiles.length === 0) return;

    this.projectiles = this.projectiles.filter((p) => {
      p.age += dt;
      p.progress = Math.min(1, p.age / p.duration);

      p.currentX = p.fromX + (p.toX - p.fromX) * p.progress;
      p.currentY = p.fromY + (p.toY - p.fromY) * p.progress;

      const screen = tileToScreen(p.currentX, p.currentY, this.cameraX, this.cameraY);

      // Arc height for ballistic flight (arrows, fireballs)
      const arcHeight = Math.sin(p.progress * Math.PI) * 24;
      const drawY = screen.y - arcHeight;

      // Add to trail
      p.trail.unshift({ x: screen.x, y: drawY, alpha: 1.0 });
      if (p.trail.length > 8) p.trail.pop();

      ctx.save();

      // Render Trail
      for (let i = 0; i < p.trail.length; i++) {
        const tr = p.trail[i];
        tr.alpha *= 0.75;
        ctx.beginPath();
        ctx.arc(tr.x, tr.y, Math.max(1, 4 - i * 0.5), 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = tr.alpha;
        ctx.fill();
      }

      ctx.globalAlpha = 1.0;

      // Render Head by Type
      if (p.type === 'arrow') {
        const dx = (p.toX - p.fromX) * (TILE_WIDTH / 2) - (p.toY - p.fromY) * (TILE_WIDTH / 2);
        const dy = (p.toX - p.fromX) * (TILE_HEIGHT / 2) + (p.toY - p.fromY) * (TILE_HEIGHT / 2) - 10;
        const angle = Math.atan2(dy, dx);

        ctx.translate(screen.x, drawY);
        ctx.rotate(angle);
        // Shaft
        ctx.fillStyle = '#854d0e';
        ctx.fillRect(-10, -1, 14, 2);
        // Arrowhead
        ctx.fillStyle = '#e2e8f0';
        ctx.beginPath();
        ctx.moveTo(4, -3);
        ctx.lineTo(8, 0);
        ctx.lineTo(4, 3);
        ctx.closePath();
        ctx.fill();
        // Fletching
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(-10, -2, 3, 4);
      } else if (p.type === 'fireball') {
        // Glowing Fireball core + halo
        const grad = ctx.createRadialGradient(screen.x, drawY, 2, screen.x, drawY, 10);
        grad.addColorStop(0, '#ffffff');
        grad.addColorStop(0.3, '#fef08a');
        grad.addColorStop(0.7, '#f97316');
        grad.addColorStop(1, 'rgba(239, 68, 68, 0)');

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(screen.x, drawY, 10, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.type === 'lightning') {
        // Jagged electric spark
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(screen.x - 6, drawY - 4);
        ctx.lineTo(screen.x, drawY);
        ctx.lineTo(screen.x + 6, drawY + 3);
        ctx.stroke();
      } else if (p.type === 'ki_blast') {
        // Golden Ki orb with outer pulse
        ctx.fillStyle = '#fbbf24';
        ctx.beginPath();
        ctx.arc(screen.x, drawY, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      } else if (p.type === 'sound_wave') {
        // Bard Sonic Ripple
        ctx.strokeStyle = '#2dd4bf';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(screen.x, drawY, 8, -Math.PI / 3, Math.PI / 3);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(screen.x - 4, drawY, 12, -Math.PI / 3, Math.PI / 3);
        ctx.stroke();
      } else {
        // Default Arcane Bolt
        ctx.fillStyle = '#a855f7';
        ctx.beginPath();
        ctx.arc(screen.x, drawY, 5, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();

      return p.progress < 1.0;
    });
  }

  private renderEntity(ctx: CanvasRenderingContext2D, ent: EntityState, time: number): void {
    const pos = tileToScreen(ent.x, ent.y, this.cameraX, this.cameraY);

    // Resource Node Rendering (Trees, Ores, Herbs, Campfire)
    if (ent.category === 'resource' && ent.resourceType) {
      if (ent.isDead) {
        // 1. Depleted Visual: Stump, Broken rubble, or tiny sprout
        ctx.save();
        if (ent.resourceType === 'tree') {
          // Handcrafted Tree Stump with wood rings
          ctx.fillStyle = '#451a03';
          ctx.beginPath();
          ctx.ellipse(pos.x, pos.y - 2, 12, 6, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#78350f';
          ctx.beginPath();
          ctx.ellipse(pos.x, pos.y - 5, 10, 5, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#b45309';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.ellipse(pos.x, pos.y - 5, 6, 3, 0, 0, Math.PI * 2);
          ctx.stroke();
        } else if (ent.resourceType === 'rock_ore') {
          // Broken ore rubble
          ctx.fillStyle = '#334155';
          ctx.fillRect(pos.x - 8, pos.y - 4, 6, 4);
          ctx.fillRect(pos.x + 2, pos.y - 6, 7, 5);
          ctx.fillStyle = '#64748b';
          ctx.fillRect(pos.x - 3, pos.y - 2, 5, 3);
        } else if (ent.resourceType === 'herb_plant') {
          // Tiny green sprout waiting to grow
          ctx.fillStyle = '#84cc16';
          ctx.beginPath();
          ctx.arc(pos.x, pos.y - 3, 3, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.globalAlpha = 0.35;
          this.sprites.drawWorldObject(ctx, ent.resourceType, pos.x, pos.y, time);
        }
        ctx.restore();

        // 2. Overhead Respawn Countdown Timer Badge
        const remaining = ent.respawnRemaining || 0;
        const total = ent.respawnTotal || 8.0;
        const progress = Math.max(0, Math.min(1, 1 - remaining / total));

        const badgeW = 44;
        const badgeH = 12;
        const badgeX = pos.x - badgeW / 2;
        const badgeY = pos.y - 26;

        ctx.save();
        ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect ? ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 3) : ctx.rect(badgeX, badgeY, badgeW, badgeH);
        ctx.fill();
        ctx.stroke();

        // Little progress line inside badge
        ctx.fillStyle = '#22c55e';
        ctx.fillRect(badgeX + 1, badgeY + badgeH - 2, (badgeW - 2) * progress, 1.5);

        ctx.font = 'bold 8px monospace';
        ctx.fillStyle = '#f8fafc';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`⏳ ${remaining.toFixed(1)}s`, pos.x, badgeY + badgeH / 2 - 1);
        ctx.restore();
        return;
      }

      // Normal or Sprouting Node Rendering
      ctx.save();
      if (ent.growAnimTimer && ent.growAnimTimer > 0) {
        // Sprout bounce scale animation (0.8s)
        const bounce = 1.0 + Math.sin((ent.growAnimTimer / 0.8) * Math.PI) * 0.28;
        ctx.translate(pos.x, pos.y);
        ctx.scale(bounce, bounce);
        ctx.translate(-pos.x, -pos.y);

        // Green sparkles around growing tree
        ctx.fillStyle = '#4ade80';
        ctx.shadowColor = '#86efac';
        ctx.shadowBlur = 6;
        for (let i = 0; i < 4; i++) {
          const ang = (i / 4) * Math.PI * 2 + time * 0.005;
          ctx.fillRect(pos.x + Math.cos(ang) * 16, pos.y - 20 + Math.sin(ang) * 10, 2, 2);
        }
      }

      this.sprites.drawWorldObject(ctx, ent.resourceType, pos.x, pos.y, time);
      if (ent.resourceType === 'rock_ore' || ent.resourceType === 'herb_plant') {
        this.renderResourceGlitter(ctx, pos.x, pos.y, time, ent.resourceType);
      }
      ctx.restore();

      // Mini Durability Bar if partially harvested
      if (ent.resources.hp < ent.resources.maxHp) {
        const barWidth = 32;
        const barHeight = 3;
        const barX = pos.x - barWidth / 2;
        const barY = pos.y - 38;
        const hpPct = Math.max(0, ent.resources.hp / ent.resources.maxHp);
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(barX - 1, barY - 1, barWidth + 2, barHeight + 2);
        ctx.fillStyle = '#f59e0b';
        ctx.fillRect(barX, barY, barWidth * hpPct, barHeight);
      }
      return;
    }

    // 1. Draw Handcrafted Pixel / Procedural Body using SpriteRenderer
    this.sprites.drawEntity(ctx, ent, pos.x, pos.y, time);

    if (ent.isDead) return;

    // 2. Name & Title label (캐릭터 머리 바로 위에 이름 배치)
    const baseHeadY = pos.y - (ent.isMounted ? 68 : 54);
    const nameY = baseHeadY;

    if (ent.category === 'npc') {
      ctx.font = 'bold 8.5px sans-serif';
      ctx.fillStyle = '#38bdf8';
      ctx.textAlign = 'center';
      ctx.fillText(`[NPC · 🎒상점/인벤토리 클릭]`, pos.x, nameY - 13);
    } else if (ent.appearance?.titlePrefix && (ent.category === 'player' || ent.category === 'companion')) {
      ctx.font = 'bold 8.5px sans-serif';
      ctx.fillStyle = '#fef08a';
      ctx.textAlign = 'center';
      ctx.fillText(`<${ent.appearance.titlePrefix}>${ent.isMounted ? ' 🐎' : ''}`, pos.x, nameY - 13);
    }
    ctx.font = 'bold 10px sans-serif';
    ctx.fillStyle = ent.category === 'npc' ? '#fde047' : '#f8fafc';
    ctx.textAlign = 'center';
    ctx.fillText(`${ent.avatarIcon || ''} ${ent.name}`, pos.x, nameY);

    // 3. Mini HP bar (발밑: 이름표와 겹치지 않도록 캐릭터 발 아래에 배치)
    const barWidth = 38;
    const barHeight = 4.5;
    const barX = pos.x - barWidth / 2;
    const barY = pos.y + 8;

    if (!ent.isMounted) { // 탑승 중에는 HP 바/시전 바 숨김
    const hpPct = Math.max(0, Math.min(1, ent.resources.hp / ent.resources.maxHp));
    ctx.fillStyle = 'rgba(0,0,0,0.75)';
    ctx.fillRect(barX - 1, barY - 1, barWidth + 2, barHeight + 2);

    ctx.fillStyle =
      ent.category === 'monster'
        ? '#ef4444'
        : ent.category === 'npc'
        ? '#38bdf8'
        : hpPct > 0.4
        ? '#22c55e'
        : '#f59e0b';
    ctx.fillRect(barX, barY, barWidth * hpPct, barHeight);

    // 4. Overhead Cast Bar if casting (HP 바 바로 위)
    if (ent.cast) {
      const castPct = ent.cast.progress / ent.cast.castTime;
      const cBarY = barY + barHeight + 4; // 발밑 HP바 아래
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(barX - 1, cBarY - 1, barWidth + 2, 5);
      ctx.fillStyle = '#38bdf8';
      ctx.fillRect(barX, cBarY, barWidth * castPct, 3);
      ctx.font = 'bold 8px monospace';
      ctx.fillStyle = '#fef08a';
      ctx.textAlign = 'center';
      ctx.fillText(ent.cast.skillName, pos.x, cBarY + 14);
    }
    }

    // 5. Overhead CC Badge
    if (ent.cc) {
      ctx.save();
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      let ccText = '⚡ STUN';
      let ccColor = '#eab308';
      if (ent.cc.type === 'mez') {
        ccText = '💤 MEZ';
        ccColor = '#a855f7';
      } else if (ent.cc.type === 'root') {
        ccText = '🕸️ ROOT';
        ccColor = '#22c55e';
      }
      ctx.fillStyle = ccColor;
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 4;
      ctx.fillText(`${ccText} (${ent.cc.remaining.toFixed(1)}s)`, pos.x, nameY - 27);
      ctx.restore();
    }

    // 6. Active Tile Property / DOT Status Badge
    if (ent.dotEffect && ent.dotEffect.remaining > 0) {
      ctx.font = 'bold 9px sans-serif';
      ctx.fillStyle = '#ef4444';
      ctx.textAlign = 'center';
      ctx.fillText(
        `${ent.dotEffect.sourceName} (-${ent.dotEffect.damagePerSec}/s · ${ent.dotEffect.remaining.toFixed(1)}s)`,
        pos.x,
        nameY - 40
      );
    }
    if (ent.category === 'player') {
      const tileEff = evaluateTileMovementEffect(this.activeZoneData, ent.x, ent.y, ent.vx, ent.vy);
      if (tileEff.spec.type !== 'grass' || tileEff.slopeState !== 'flat') {
        ctx.font = 'bold 8.5px sans-serif';
        ctx.fillStyle = tileEff.spec.badgeColor;
        ctx.textAlign = 'center';
        ctx.fillText(`[타일: ${tileEff.statusLabelKr}]`, pos.x, nameY - 53);
      }
    }
  }

  private renderFloatingTexts(ctx: CanvasRenderingContext2D, dt: number): void {
    if (this.floatingTexts.length === 0) return;

    this.floatingTexts = this.floatingTexts.filter((ft) => {
      ft.age += dt;
      if (ft.age >= ft.lifetime) return false;

      const progress = ft.age / ft.lifetime;
      const alpha = 1 - progress;
      const floatUpOffset = progress * 28;

      const pos = tileToScreen(ft.x, ft.y, this.cameraX, this.cameraY);

      ctx.save();
      ctx.font = `bold ${ft.size}px monospace, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillStyle = ft.color;
      ctx.globalAlpha = alpha;
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 4;
      ctx.fillText(ft.text, pos.x, pos.y - 40 - floatUpOffset);
      ctx.restore();

      return true;
    });
  }

  /**
   * Screen-space Zoom Badge HUD
   */
  private renderZoomHUD(ctx: CanvasRenderingContext2D): void {
    if (this.zoomDisplayTimer <= 0) return;

    const alpha = Math.min(1, this.zoomDisplayTimer);
    const text = `🔍 줌: ${(this.zoom * 100).toFixed(0)}% (휠 스크롤)`;

    ctx.save();
    ctx.font = 'bold 12px monospace, sans-serif';
    const textWidth = ctx.measureText(text).width;
    const paddingX = 12;
    const paddingY = 6;
    const w = textWidth + paddingX * 2;
    const h = 24;
    const x = this.canvas.width - w - 20;
    const y = this.canvas.height - h - 20;

    ctx.fillStyle = `rgba(15, 23, 42, ${alpha * 0.85})`;
    ctx.strokeStyle = `rgba(56, 189, 248, ${alpha * 0.6})`;
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(x, y, w, h, 6) : ctx.rect(x, y, w, h);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + w / 2, y + h / 2);
    ctx.restore();
  }
}
